import { ref, onBeforeUnmount, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { AUTH_LOGOUT } from '@/store/actions/auth';
import { clearCacheOnly } from '@/utils/CacheManager';

/**
 * useSessionTimeout - Composable para gestionar el timeout de sesión por inactividad
 * 
 * Monitorea la actividad del usuario y cierra la sesión automáticamente
 * después de 60 minutos de inactividad.
 * 
 * @param {number} timeoutMinutes - Minutos de inactividad antes de cerrar sesión (default: 60)
 * @returns {Object} - Objeto con estado y métodos de control
 */
export function useSessionTimeout(timeoutMinutes = 60) {
  const store = useStore();
  const router = useRouter();
  
  // Convertir minutos a milisegundos
  const TIMEOUT_MS = timeoutMinutes * 60 * 1000;
  
  // Estado
  const lastActivity = ref(Date.now());
  const isActive = ref(true);
  
  // Timers
  let activityTimer = null;
  let checkTimer = null;
  
  /**
   * Actualiza el timestamp de última actividad
   */
  const updateActivity = () => {
    lastActivity.value = Date.now();
    isActive.value = true;
  };
  
  /**
   * Cierra la sesión por inactividad
   */
  const logoutDueToInactivity = async () => {
    isActive.value = false;
    
    // Limpiar timers
    stopMonitoring();
    
    try {
      // Limpiar cache
      await clearCacheOnly();
      
      // Dispatch del logout
      await store.dispatch(AUTH_LOGOUT);
      
      // Redirigir al login con mensaje
      router.push({ 
        name: 'Login',
        query: { reason: 'inactivity' }
      });
      
      // Mostrar mensaje al usuario si hay un servicio de toast disponible
      if (window.$toast) {
        window.$toast.add({
          severity: 'warn',
          summary: 'Sesión expirada',
          detail: 'Tu sesión ha caducado por inactividad',
          life: 5000
        });
      }
    } catch (error) {
      console.error('Error al cerrar sesión por inactividad:', error);
    }
  };
  
  /**
   * Verifica si ha pasado el tiempo de inactividad
   */
  const checkInactivity = () => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivity.value;
    
    if (timeSinceLastActivity >= TIMEOUT_MS) {
      logoutDueToInactivity();
    }
  };
  
  /**
   * Inicia el monitoreo de actividad
   */
  const startMonitoring = () => {
    // Si ya hay un timer activo, limpiarlo primero
    if (checkTimer) {
      clearInterval(checkTimer);
      checkTimer = null;
    }
    
    // Eventos que indican actividad del usuario
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];
    
    // Throttle para no actualizar en cada evento (optimización)
    let throttleTimer = null;
    const throttledUpdate = () => {
      if (!throttleTimer) {
        throttleTimer = setTimeout(() => {
          updateActivity();
          throttleTimer = null;
        }, 1000); // Actualizar máximo cada segundo
      }
    };
    
    // Agregar listeners
    events.forEach(event => {
      window.addEventListener(event, throttledUpdate);
    });
    
    // Guardar referencia para poder remover después
    activityTimer = { events, handler: throttledUpdate };
    
    // Verificar inactividad cada 10 minutos
    checkTimer = setInterval(checkInactivity, 600000);
    //5000 5 segundos para pruebas rápidas, cambiar a 600000 (10 minutos) en producción
  };
  
  /**
   * Detiene el monitoreo de actividad
   */
  const stopMonitoring = () => {
    if (activityTimer) {
      activityTimer.events.forEach(event => {
        window.removeEventListener(event, activityTimer.handler);
      });
      activityTimer = null;
    }
    
    if (checkTimer) {
      clearInterval(checkTimer);
      checkTimer = null;
    }
  };
  
  /**
   * Resetea el timer de inactividad (útil después de acciones importantes)
   */
  const resetTimer = () => {
    updateActivity();
  };
  
  /**
   * Obtiene el tiempo restante hasta el timeout (en minutos)
   */
  const getRemainingTime = () => {
    const timeSinceLastActivity = Date.now() - lastActivity.value;
    const remainingMs = Math.max(0, TIMEOUT_MS - timeSinceLastActivity);
    return Math.ceil(remainingMs / (60 * 1000));
  };
  
  // NO usar onMounted porque App.vue ya está montado antes del login
  // El monitoreo se inicia manualmente desde AppLayout.vue después del login completo
  
  onBeforeUnmount(() => {
    stopMonitoring();
  });
  
  return {
    isActive,
    lastActivity,
    startMonitoring,
    stopMonitoring,
    resetTimer,
    getRemainingTime,
    updateActivity
  };
}

export default useSessionTimeout;
